import { SqlProcessKind } from '@common/graph/types';
import { isCaseInsensitiveEqual } from '@prophecy/utils/string';

import { Providers, ProviderType } from '../../schema/types';
import { ExampleProject } from '../types';
import { Entity } from '../types/Entity';
import { TableType } from './types';

const doesListHasKeyword = (list: readonly string[], word: string) => {
  return list.some((keyword) => isCaseInsensitiveEqual(keyword, word));
};

export const SQL_RESERVED_KEYWORDS = [
  'SELECT',
  'FROM',
  'WHERE',
  'ORDER',
  'GROUP',
  'HAVING',
  'LIMIT',
  'JOIN',
  'ASC',
  'DESC',
  'DISTINCT',
  'INNER',
  'FULL',
  'CROSS',
  'SEMI',
  'ANTI',
  'EXISTS',
  'INTERSECT',
  'MINUS',
  'EXCEPT',
  'UNION',
  'CASE',
  'WHEN',
  'THEN',
  'ELSE',
  'BETWEEN',
  'ILIKE',
  'LIKE',
  'NULL',
  'INTERVAL',
  'LEFT',
  'RIGHT',
  'OUTER',
  'WITH',
  'TIMESTAMP',
  'DATETIME',
  'BY',
  'ON',
  'AND',
  'NOT',
  'ANY',
  'ALL',
  'END',
  'IS',
  'OVER',
  'ROWS',
  'RANGE',
  'PRECEDING',
  'CURRENT',
  'FOLLOWING',
  'FORMAT',
  'ARRAY',
  'PARTITION',
  'UNBOUNDED',
  'JSON',
  'TYPE',
  'CASE',
  'WHEN',
  'THEN',
  'ELSE',
  'BETWEEN',
  'LIKE',
  'INTERVAL',
  'WITH',
  'IGNORE',
  'RESPECT',
  'VERSION',
  'FILTER',
  'CLUSTER',
  'DISTRIBUTE',
  'ROLLUP',
  'CUBE',
  'GROUPING',
  'SETS',
  'QUALIFY',
  'LATERAL',
  'VIEW',
  'OFFSET',
  'TABLESAMPLE',
  'SORT',
  'WINDOW',
  'FETCH',
  'AS',
  'OR',
  'NOT IN',
  'IN',
  'AND',
  'ON',
  'OF',
  'OVER',
  'BY',
  'NATURAL'
] as const;

export function isSqlReservedKeyword(word: string) {
  return doesListHasKeyword(SQL_RESERVED_KEYWORDS, word);
}

const SQL_RESERVED_KEYWORDS_FOR_PROVIDER: { [key in Providers | 'default']?: readonly string[] } = {
  default: [
    'BY',
    'ON',
    'OR',
    'FOLLOWING',
    'ROWS',
    'UNION',
    'ORDER',
    'IN',
    'AND',
    'AS',
    'WHEN',
    'MINUS',
    'NULL',
    'INTERSECT',
    'BETWEEN',
    'NOT',
    'OF',
    'GROUP',
    'FROM',
    'SELECT',
    'ALL',
    'THEN',
    'EXISTS',
    'CHECK',
    'WHERE',
    'VIEW',
    'ELSE',
    'HAVING',
    'DISTINCT',
    'ANY',
    'IS',
    'CASE',
    'WITH',
    'LIKE',
    'CURRENT',
    'INT',
    'RLIKE',
    'FOR',
    'NUMERIC',
    'NATURAL',
    'EXTRACT',
    'USING'
  ],
  [Providers.Snowflake]: ['ILIKE', 'QUALIFY', 'TABLESAMPLE']
};

export const isSQLReservedKeywordForProvider = (word: string, provider?: ProviderType) => {
  return (
    (provider && doesListHasKeyword(SQL_RESERVED_KEYWORDS_FOR_PROVIDER[provider] || [], word)) ||
    doesListHasKeyword(SQL_RESERVED_KEYWORDS_FOR_PROVIDER.default || [], word)
  );
};

export function validateTableName(value: string = '') {
  if (isSqlReservedKeyword(value)) {
    return 'Reserved keywords are not allowed in table name';
  } else if (!value.match(/^[a-zA-Z]/)) {
    return 'Table name should start with a letter';
    // eslint-disable-next-line no-useless-escape
  } else if (!value.match(/^[\w\.]+$/)) {
    return `Table name should contain only alphanumeric, underscore and .`;
  }
}

export function generateSQLExampleLinksMap(exampleProjectData?: ExampleProject) {
  const exampleLinksMap: Record<string, string> = {};
  exampleProjectData?.sqlOrchestratorPipelines?.forEach((pipeline) => {
    const gemName = pipeline.name.toLowerCase().replace(/(.*)_(n|e|example(s)?)$/, '$1');
    exampleLinksMap[gemName] = `/sql/${exampleProjectData._id}?entity=pipeline&name=${pipeline.name}`;
  });
  return exampleLinksMap;
}

export function getTableId({
  tableType,
  sourceName: _sourceName,
  name: _name
}: {
  tableType: TableType;
  sourceName?: string;
  name: string;
}) {
  let tableId: string = '';
  const name = _name.replace(/\s/g, '_');
  const sourceName = _sourceName?.replaceAll('-', '_');
  if (tableType === Entity.UnreferencedSource) {
    tableId = `unreferencedTable/${name}`;
  } else if (tableType === Entity.View) {
    tableId = `view/${sourceName}/${name}`;
  } else if (tableType === Entity.Table || tableType === Entity.Source || tableType === SqlProcessKind.ExternalSource) {
    tableId = `table/${sourceName}/${name}`;
  } else if (tableType === Entity.Seed) {
    tableId = `seed/${name}`;
  }
  if (tableId) {
    tableId = tableId.replaceAll('.', '_prophecy_dot_');
  }
  return tableId;
}
